/**
 * Reading the height and weight a member types.
 *
 * The same value arrives in several shapes and every screen used to interpret
 * it differently: onboarding sent "1,75" to a smallint column and showed the
 * member the resulting Postgres error, the profile page rounded it to 2cm, and
 * the settings page let it through unchanged. One implementation, so the app
 * gives one answer.
 *
 * These bounds mirror the server's validation deliberately. A value this
 * accepts and the API then rejects is the worst of both worlds.
 */
export const MIN_HEIGHT_CM = 50;
export const MAX_HEIGHT_CM = 260;
export const MIN_WEIGHT_KG = 20;
export const MAX_WEIGHT_KG = 400;

/** A number from "80,5" or "80.5"; null when it isn't one. */
const toNumber = (raw) => {
  const value = Number(String(raw ?? "").replace(",", ".").trim());
  return Number.isFinite(value) && value > 0 ? value : null;
};

/**
 * Height in whole centimetres, however it was written.
 *
 * Metres are recognised by size rather than by the separator — nobody is 3cm
 * tall and nobody is 3 metres — so "1,75", "1.75" and "175" all give 175.
 * Fractional centimetres are rounded, because the column holds an integer.
 *
 * @returns {number|null} centimetres, or null when the input is not a number
 */
export const toCentimetres = (raw) => {
  const value = toNumber(raw);
  if (value === null) return null;
  return Math.round(value < 3 ? value * 100 : value);
};

/** Weight in kilograms, accepting either decimal separator. */
export const toKilograms = (raw) => toNumber(raw);

/** @returns {string|null} an error to show, or null when the height is usable */
export const heightError = (raw) => {
  const cm = toCentimetres(raw);
  if (cm === null) return "Inserisci un’altezza valida.";
  if (cm < MIN_HEIGHT_CM || cm > MAX_HEIGHT_CM) {
    return `L’altezza deve essere tra ${MIN_HEIGHT_CM} e ${MAX_HEIGHT_CM} cm.`;
  }
  return null;
};

/** @returns {string|null} an error to show, or null when the weight is usable */
export const weightError = (raw, label = "Il peso") => {
  const kg = toKilograms(raw);
  if (kg === null) return `Inserisci un ${label.toLowerCase().replace(/^il /, "")} valido.`;
  if (kg < MIN_WEIGHT_KG || kg > MAX_WEIGHT_KG) {
    return `${label} deve essere tra ${MIN_WEIGHT_KG} e ${MAX_WEIGHT_KG} kg.`;
  }
  return null;
};
